import React, { useId } from "react";
import { motion } from "framer-motion";

export interface SegmentedSwitchOption<T = string> {
    id: T;
    label: React.ReactNode;
    icon?: React.ReactNode;
    disabled?: boolean;
}

export interface SegmentedSwitchProps<T = string> {
    options: SegmentedSwitchOption<T>[];
    value: T;
    onChange: (value: T) => void;
    className?: string;
    tabClassName?: string;
    pillClassName?: string;
    layoutId?: string;
    fullWidth?: boolean;
}

function SegmentedSwitch<T = string>({
    options,
    value,
    onChange,
    className = "",
    tabClassName = "",
    pillClassName = "",
    layoutId,
    fullWidth = true,
}: SegmentedSwitchProps<T>) {
    const generatedId = useId();
    const activeLayoutId = layoutId || `segmented-switch-${generatedId}`;

    return (
        <div
            role="tablist"
            className={`relative p-1 rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/80 flex items-center ${fullWidth ? "w-full" : "w-fit"
                } ${className}`}
        >
            {options.map((option) => {
                const isActive = option.id === value;

                return (
                    <button
                        key={String(option.id)}
                        role="tab"
                        type="button"
                        aria-selected={isActive}
                        disabled={option.disabled}
                        onClick={() => !option.disabled && onChange(option.id)}
                        className={`relative z-10 flex-1 py-2 px-3.5 flex items-center justify-center gap-2 text-sm transition-colors duration-200 select-none cursor-pointer rounded-xl ${option.disabled ? "opacity-50 cursor-not-allowed" : ""
                            } ${isActive
                                ? "text-secondary font-medium"
                                : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 font-normal"
                            } ${tabClassName}`}
                    >
                        {/* iOS-style animated sliding pill indicator */}
                        {isActive && (
                            <motion.div
                                layoutId={activeLayoutId}
                                transition={{
                                    type: "spring",
                                    stiffness: 450,
                                    damping: 35,
                                }}
                                className={`absolute inset-0 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-700 pointer-events-none ${pillClassName}`}
                            />
                        )}

                        {/* Content */}
                        <span className="relative z-10 flex items-center gap-2">
                            {option.icon && (
                                <span className={`shrink-0 transition-colors duration-200 ${isActive ? "text-secondary" : "text-gray-500 dark:text-gray-400"}`}>
                                    {option.icon}
                                </span>
                            )}
                            <span className="truncate">{option.label}</span>
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

export default SegmentedSwitch;
